import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Device } from "@twilio/voice-sdk";
import { Phone, PhoneOff, PhoneCall, X, MessagesSquare, MessageSquare, Inbox } from "lucide-react";
import { toast } from "./ui/sonner";
import { safeBottom } from "../lib/pwa";
import { useFloatingFab, panelStyleFor, FAB_SIZE } from "../lib/useFloatingFab";
import { useFloatingWindow } from "../lib/useFloatingWindow";
import { PhonePane } from "./comm/PhonePane";
import { CommRail, CallBar } from "./comm/CommRail";
import { CommTitleBar, ResizeHandles } from "./comm/CommWindowChrome";
import { ChatDockPane } from "./chat/ChatDockPane";
import { useChatStream } from "./chat/useChatStream";
import { ChatLightbox } from "./chat/Lightbox";
import { openChatPopout, popoutBus } from "../lib/commPopout";
import { PoppedOutPane } from "./comm/PoppedOutPane";
import CommunicationLayer from "./comm/CommunicationLayer";

const useMobile = () => {
  const [m, setM] = useState(() => window.innerWidth < 640);
  useEffect(() => { const f = () => setM(window.innerWidth < 640); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);
  return m;
};

/* 悬浮沟通窗（微信式）：聊天 · 拨号 · 短信 · 收件箱 四页签；网页拨号（Twilio Device）常驻注册以便来电；chatUnread 由 Layout 传入 */
export default function Softphone({ chatUnread = 0 }) {
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const { pathname } = useLocation();
  const mobile = useMobile();
  const isSub = user?.role === "subcontractor";
  const onChatPage = pathname.startsWith("/chat");
  const [open, setOpen] = useState(false);
  const openRef = useRef(false);
  openRef.current = open;
  const [tab, setTab] = useState(isSub ? "dial" : "chat");   // chat | dial | sms | inbox
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const onChatPageRef = useRef(onChatPage);
  onChatPageRef.current = onChatPage;
  const [openReq, setOpenReq] = useState(null);
  useEffect(() => { if (open && mobile) document.body.setAttribute("data-comm-open", "1"); else document.body.removeAttribute("data-comm-open"); return () => document.body.removeAttribute("data-comm-open"); }, [open, mobile]);
  const [ready, setReady] = useState(false);
  const [initing, setIniting] = useState(false);
  const [num, setNum] = useState("");
  const [status, setStatus] = useState("idle"); // idle | calling | in-call
  const [muted, setMuted] = useState(false);
  const [err, setErr] = useState("");
  const [callerId, setCallerId] = useState("");
  const [defaultCallerId, setDefaultCallerId] = useState("");
  const [callerMode, setCallerMode] = useState(null);
  const callerIdRef = useRef("");
  useEffect(() => { callerIdRef.current = callerId; }, [callerId]);
  const [cidEdit, setCidEdit] = useState(false);
  const [cidInput, setCidInput] = useState("");
  const [cidBusy, setCidBusy] = useState(false);
  const saveCallerId = async () => {
    setCidBusy(true);
    try {
      const { data } = await api.put("/me/caller-id", { callerId: cidInput.trim() });
      setCallerId(data.effective || ""); setDefaultCallerId(data.defaultCallerId || ""); setCidEdit(false);
      setCallerMode(data.callerId ? { mode: "personal", callerId: data.callerId } : null);
      api.get("/telephony/voice-token").then((r) => setCallerMode(r.data.callerMode || null)).catch(() => {});
      toast.success(data.callerId ? (zh ? `拨出显号已设为 ${data.callerId}` : `Caller ID set to ${data.callerId}`) : (zh ? "已恢复公司默认号码" : "Using company default"));
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setCidBusy(false);
  };
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const initingRef = useRef(false);
  const pendingDialRef = useRef(null);
  const ctxRef = useRef({});   // TM context (tmId / tmType / taskItemId) forwarded to the TwiML app
  const [callee, setCallee] = useState("");
  const [incoming, setIncoming] = useState(null);   // {from, name, leadId}
  const incomingRef = useRef(null);
  const [smsUnread, setSmsUnread] = useState(0);
  const smsSeen = useRef(null);
  const [replyFrom, setReplyFrom] = useState("");    // 回复来信时用来信被叫号码作为发送号
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimer = useRef(null);
  const [bulkList, setBulkList] = useState([]);   // 群发列表 [{phone,name}]
  const addBulk = (ct) => {
    const norm = normalizeE164(ct.phone);
    if (!norm) return;
    setBulkList((prev) => (prev.find((x) => x.phone === norm) ? prev : [...prev, { phone: norm, name: ct.name || "" }]));
  };

  // 悬浮按钮：拖动 / 贴边隐藏 / 悬停弹出（共用 useFloatingFab）
  const fab = useFloatingFab("softphone", { defaultPos: () => ({ x: window.innerWidth - FAB_SIZE - 24, y: window.innerHeight - safeBottom() - FAB_SIZE - 24 }), onTap: () => setOpen((o) => !o), panelOpen: open, legacyHiddenKey: "softphone_hidden", avoid: ["assistant"] });
  const pos = fab.fabPos;
  const chatAvail = !isSub && !onChatPage;
  const chatTab = tab === "chat" && chatAvail;
  const PW = chatAvail ? Math.min(760, window.innerWidth - 16) : 360;
  const PH = chatAvail ? Math.min(560, Math.round(window.innerHeight * 0.85)) : Math.min(520, Math.round(window.innerHeight * 0.85));
  // 桌面：可拖动 / 拉伸 / 最大化的窗口几何（记住位置与大小）；首次打开落在悬浮球旁
  const win = useFloatingWindow("comm_win", { defaultRect: () => { const s = panelStyleFor(pos, PW, PH); return { x: s.left ?? 8, y: s.top ?? 8, w: PW, h: PH }; }, min: { w: 380, h: 400 }, enabled: open && !mobile });
  const panelStyle = mobile ? { left: 0, top: 0, width: "100vw", height: "100dvh" } : (win.style || { ...panelStyleFor(pos, PW, PH), width: PW, height: PH });
  const closeWin = () => { setOpen(false); setOpenReq(null); setTab(isSub ? "dial" : "chat"); };

  // 聊天「弹出独立窗口」（双屏 / 大屏）：独立窗口每 2s 心跳 → popped；关闭 / 收回 → 恢复悬浮窗聊天；独立窗口里的非聊天链接 → 本页打开
  const [popped, setPopped] = useState(false);
  const poppedRef = useRef(false);
  poppedRef.current = popped;
  const popRef = useRef(null);
  const lastChatRef = useRef(null);   // 悬浮窗里最后打开的频道 → 弹出独立窗口时直接定位
  const busRef = useRef(null);
  const aliveRef = useRef(0);
  useEffect(() => {
    const bus = popoutBus();
    busRef.current = bus;
    const handle = (m) => {
      if (!m || typeof m !== "object") return;
      if (m.type === "alive") { aliveRef.current = Date.now(); setPopped(true); }
      else if (m.type === "closed") { aliveRef.current = 0; setPopped(false); }
      else if (m.type === "return") { aliveRef.current = 0; setPopped(false); fab.showPeek(); setOpen(true); setTab("chat"); }
      else if (m.type === "nav" && m.path) { nav(m.path); try { window.focus(); } catch { /* ignore */ } }
    };
    if (bus) bus.onmessage = (e) => handle(e.data);
    const onMsg = (e) => { if (e.origin === window.location.origin && e.data?.type === "ali_comm") handle(e.data.msg); };
    window.addEventListener("message", onMsg);
    const t = setInterval(() => { if (aliveRef.current && Date.now() - aliveRef.current > 6000) { aliveRef.current = 0; setPopped(false); } }, 3000);
    return () => { clearInterval(t); window.removeEventListener("message", onMsg); bus?.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toPopout = (msg) => { busRef.current?.postMessage(msg); try { popRef.current?.focus(); } catch { /* ignore */ } };
  const popOut = (target) => { popRef.current = openChatPopout(target); if (popRef.current) { aliveRef.current = Date.now(); setPopped(true); } else toast.error(zh ? "浏览器拦截了弹出窗口，请允许本站弹窗后重试" : "Pop-up blocked — allow pop-ups for this site"); };
  const popReturn = () => { toPopout({ type: "close" }); try { popRef.current?.close(); } catch { /* ignore */ } popRef.current = null; aliveRef.current = 0; setPopped(false); setTab("chat"); };

  // 全局聊天流：沟通窗关闭 / 不在聊天页签且不在 /chat 页 → 右下角 toast「频道 · 发送人：内容」，点「查看」直达该频道；自己发的不提醒
  const onChatEvent = useCallback((ev) => {
    window.dispatchEvent(new CustomEvent("chat:event", { detail: ev }));   // 项目管理页「项目讨论」等据此自动刷新
    if (ev.event === "remind") {   // 消息提醒到点：任意页面右下角 toast，点「查看」直达该消息（铃铛由后端同步写入）
      const r = ev.data || {};
      toast(zh ? "⏰ 聊天提醒" : "⏰ Chat reminder", { id: `remind-${r.reminderId || r.messageId}`, duration: 15000,
        description: `${r.author ? `${r.author}：` : ""}${(r.preview || "").slice(0, 100)}${r.channelName ? ` · ${r.channelName}` : ""}${r.note ? (zh ? ` · 备注：${r.note}` : ` · ${r.note}`) : ""}`,
        action: { label: zh ? "查看" : "Open", onClick: () => window.dispatchEvent(new CustomEvent("chat:open", { detail: { channelId: r.channelId, messageId: r.messageId } })) } });
      window.dispatchEvent(new Event("reminders-changed"));
      return;
    }
    if (ev.event !== "message") return;
    const d = ev.data || {}; const m = d.message || {};
    if (!m.id || m.authorUsername === user?.username) return;
    window.dispatchEvent(new Event("chat-unread-changed"));
    if (onChatPageRef.current || poppedRef.current || (openRef.current && tabRef.current === "chat")) return;
    const who = (zh ? m.authorNameZh : m.authorName) || m.authorName || m.authorNameZh || m.authorUsername;
    const chName = (zh ? d.channelNameZh : d.channelName) || d.channelName || d.channelNameZh || "";
    const body = (m.body || "").trim() || (m.type === "voice" ? (zh ? `[语音 ${Math.round(m.voice?.duration || 0)}"]` : `[voice ${Math.round(m.voice?.duration || 0)}"]`) : m.attachments?.length ? (zh ? "[附件]" : "[attachment]") : `[${m.type || "msg"}]`);
    toast(`${chName ? `${chName} · ` : ""}${who}`, { id: `chat-${m.id}`, description: body.length > 120 ? `${body.slice(0, 120)}…` : body, duration: 8000,
      action: { label: zh ? "查看" : "Open", onClick: () => window.dispatchEvent(new CustomEvent("chat:open", { detail: { channelId: d.channelId, messageId: m.id } })) } });
  }, [user?.username, zh]);
  useChatStream({ enabled: !!user && !isSub, onEvent: onChatEvent });

  const initDevice = useCallback(async () => {
    if (deviceRef.current || initingRef.current) return;
    initingRef.current = true; setIniting(true); setErr("");
    try {
      const { data } = await api.get("/telephony/voice-token");
      if (!data.ok) { setErr(data.hint || "网页拨号未配置"); return; }
      setCallerId(data.callerId || ""); setDefaultCallerId(data.defaultCallerId || ""); setCallerMode(data.callerMode || null);
      const device = new Device(data.token, { codecPreferences: ["opus", "pcmu"], logLevel: "error" });
      device.on("registered", () => setReady(true));
      device.on("error", (e) => setErr(e?.message || "device error"));
      device.on("tokenWillExpire", async () => {
        try { const { data: d2 } = await api.get("/telephony/voice-token"); if (d2.ok) device.updateToken(d2.token); } catch { /* ignore */ }
      });
      device.on("incoming", (call) => {
        const cp = call.customParameters || new Map();
        const from = (cp.get && cp.get("fromNumber")) || call.parameters?.From || "";
        const name = (cp.get && cp.get("leadName")) || "";
        const leadId = (cp.get && cp.get("leadId")) || "";
        incomingRef.current = call;
        setIncoming({ from, name, leadId });
        setOpen(true); fab.showPeek();
        call.on("cancel", () => { setIncoming(null); incomingRef.current = null; });
        call.on("reject", () => { setIncoming(null); incomingRef.current = null; });
        call.on("disconnect", () => { setIncoming(null); incomingRef.current = null; callRef.current = null; setStatus("idle"); setMuted(false); });
        call.on("accept", () => { setIncoming(null); callRef.current = call; setStatus("in-call"); setCallee(name || from); setTab("dial"); });
        call.on("error", (e) => setErr(friendlyErr(e?.message)));
      });
      await device.register();
      deviceRef.current = device;
    } catch (e) { setErr(e?.message || "初始化失败"); }
    finally { initingRef.current = false; setIniting(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  // Register on mount so inbound calls can ring even with the panel closed.
  useEffect(() => { initDevice(); }, [initDevice]);
  // 首次用户手势后恢复 SDK 共享 AudioContext，避免后续铃声/拨号音刷「AudioContext was not allowed」警告
  useEffect(() => {
    const unlock = () => { try { const ac = Device.audioContext; if (ac?.state === "suspended") ac.resume(); } catch { /* ignore */ } };
    ["pointerdown", "keydown"].forEach((ev) => window.addEventListener(ev, unlock, { once: true, capture: true, passive: true }));
    return () => ["pointerdown", "keydown"].forEach((ev) => window.removeEventListener(ev, unlock, { capture: true }));
  }, []);
  // Destroy device on unmount OR when the logged-in account changes (avoid stale identity/token).
  useEffect(() => () => {
    try { deviceRef.current?.destroy(); } catch { /* ignore */ }
    deviceRef.current = null; setReady(false);
  }, [user?.username]);

  // Presence heartbeat + live status while registered → inbound ringing + dispatch board.
  const presenceRef = useRef({ status: "idle", peer: "" });
  useEffect(() => {
    const st = incoming ? "ringing" : (status === "in-call" ? "in-call" : status === "calling" ? "calling" : "idle");
    presenceRef.current = { status: st, peer: (incoming && incoming.from) || callee || "" };
    if (ready) api.post("/telephony/presence", presenceRef.current).catch(() => {});
  }, [status, incoming, ready, api, callee]);
  useEffect(() => {
    if (!ready) return undefined;
    const ping = () => { api.post("/telephony/presence", presenceRef.current).catch(() => {}); };
    const t = setInterval(ping, 20000);
    return () => clearInterval(t);
  }, [ready, api]);
  // 短信收件箱未读数（登录即轮询）；面板关闭时有新来信 → 下次打开直接落在「收件箱」
  useEffect(() => {
    if (!user) return undefined;
    const pull = () => { api.get("/telephony/sms-inbox", { params: { days: 30, unread: 1, limit: 1 } }).then(({ data }) => { const n = data.unread || 0; if (smsSeen.current !== null && n > smsSeen.current && !openRef.current) setTab("inbox"); smsSeen.current = n; setSmsUnread(n); }).catch(() => {}); };
    pull();
    const t = setInterval(pull, 30000);
    return () => clearInterval(t);
  }, [user?.username, api]); // eslint-disable-line

  const acceptIncoming = () => { try { incomingRef.current?.accept(); } catch { /* ignore */ } };
  const rejectIncoming = () => { try { incomingRef.current?.reject(); } catch { /* ignore */ } setIncoming(null); incomingRef.current = null; };

  // Best-effort E.164 normalize: strip spaces/dashes, auto +1 for 10-digit US numbers.
  const normalizeE164 = (raw) => {
    const s = String(raw || "").trim();
    const hasPlus = s.startsWith("+");
    const digits = s.replace(/\D/g, "");
    if (!digits) return "";
    if (hasPlus) return "+" + digits;
    if (digits.length === 10) return "+1" + digits;        // US local → +1
    if (digits.length === 11 && digits.startsWith("1")) return "+" + digits;
    return "+" + digits;
  };
  const isValidE164 = (v) => /^\+[1-9]\d{9,14}$/.test(v);
  const friendlyErr = (msg) => {
    const m = String(msg || "");
    if (m.includes("31005") || m.includes("HANGUP"))
      return zh ? "呼叫被挂断：请确认号码正确（美国号码为 +1 加 10 位数字），或对方无法接通。"
                : "Call hung up: check the number (US = +1 and 10 digits) or the callee is unreachable.";
    if (m.includes("31000") || m.includes("Access Token"))
      return zh ? "连接令牌失效，请关闭面板重新打开。" : "Token expired — reopen the panel.";
    return m || (zh ? "通话错误" : "Call error");
  };

  const call = async (override) => {
    const to = normalizeE164(override != null ? override : num);
    if (!to || !deviceRef.current) return;
    if (!isValidE164(to)) {
      setErr(zh ? `号码格式无效：${to}。请输入 +1 加 10 位数字（例：+16265551234）`
                : `Invalid number: ${to}. Use +1 and 10 digits.`);
      return;
    }
    setNum(to);
    setErr("");
    try {
      const ctx = { ...ctxRef.current };
      const startedAt = new Date().toISOString();
      const c = await deviceRef.current.connect({ params: { To: to, ...(callerIdRef.current ? { CallerId: callerIdRef.current } : {}), ...ctxRef.current } });
      ctxRef.current = {};
      callRef.current = c;
      setStatus("calling");
      // TM 今日呼出等页面监听：挂断后触发录音 → 转写 → AI 建议轮询
      const ended = (how) => window.dispatchEvent(new CustomEvent("softphone:ended", { detail: { ...ctx, to, how, startedAt, endedAt: new Date().toISOString(), callSid: c?.parameters?.CallSid || null } }));
      c.on("accept", () => setStatus("in-call"));
      c.on("disconnect", () => { setStatus("idle"); setMuted(false); callRef.current = null; setCallee(""); ended("disconnect"); });
      c.on("cancel", () => { setStatus("idle"); callRef.current = null; setCallee(""); ended("cancel"); });
      c.on("error", (e) => { setErr(friendlyErr(e?.message)); setStatus("idle"); });
    } catch (e) { setErr(friendlyErr(e?.message) || "拨打失败"); }
  };
  const hangup = () => { try { callRef.current?.disconnect(); deviceRef.current?.disconnectAll(); } catch { /* ignore */ } setStatus("idle"); };
  const toggleMute = () => { const c = callRef.current; if (!c) return; const m = !muted; c.mute(m); setMuted(m); };

  // Debounced contact search (leads + staff) by name/phone/address.
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const term = search.trim();
    if (term.length < 1) { setResults([]); setSearching(false); return undefined; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/telephony/contacts/search?q=${encodeURIComponent(term)}`);
        setResults(data.contacts || []); setShowResults(true);
      } catch { setResults([]); }
      setSearching(false);
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [search, api]);

  const pickContact = (ct) => {
    const norm = normalizeE164(ct.phone);
    setNum(norm); setCallee(ct.name || ""); setSearch(""); setResults([]); setShowResults(false); setErr("");
  };

  // Global buses: `softphone:dial` / `softphone:sms`（页面各处拨打 / 短信按钮）· `chat:open`（项目页「打开项目群」等 → 悬浮窗聊天页签）
  useEffect(() => {
    const handler = (e) => {
      const raw = e?.detail?.number || "";
      const norm = normalizeE164(raw);
      if (!norm) return;
      setCallee(e?.detail?.name || "");
      const d = e?.detail || {};
      ctxRef.current = Object.fromEntries(["tmId", "tmType", "taskItemId"].filter((k) => d[k]).map((k) => [k, String(d[k])]));
      setNum(norm);
      setErr("");
      fab.showPeek();
      setOpen(true); setTab("dial");
      if (status !== "idle") return;         // already on a call; just prefill
      if (deviceRef.current && ready) { call(norm); }
      else { pendingDialRef.current = norm; }  // dial once device registers
    };
    window.addEventListener("softphone:dial", handler);
    const smsHandler = (e) => {
      const norm = normalizeE164(e?.detail?.number || "");
      if (!norm) return;
      setCallee(e?.detail?.name || ""); setNum(norm); setErr(""); fab.showPeek(); setOpen(true); setTab("sms");
    };
    window.addEventListener("softphone:sms", smsHandler);
    const chatHandler = (e) => {
      const d = e?.detail || {};
      if (poppedRef.current) { toPopout({ type: "open", channelId: d.channelId || null, messageId: d.messageId || null }); return; }   // 已弹出 → 在独立窗口里定位
      setOpenReq({ channelId: d.channelId || null, messageId: d.messageId || null, n: Date.now() });
      fab.showPeek(); setOpen(true); setTab("chat");
    };
    window.addEventListener("chat:open", chatHandler);
    return () => { window.removeEventListener("softphone:dial", handler); window.removeEventListener("softphone:sms", smsHandler); window.removeEventListener("chat:open", chatHandler); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, status]);

  // Auto-dial the queued number once the device becomes ready.
  useEffect(() => {
    if (ready && status === "idle" && pendingDialRef.current) {
      const n = pendingDialRef.current; pendingDialRef.current = null;
      call(n);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, status]);

  const totalBadge = (isSub ? 0 : chatUnread) + smsUnread;
  const railItems = [
    !isSub && !onChatPage && ["chat", MessagesSquare, zh ? "聊天" : "Chat", chatUnread, "softphone-tab-chat"],
    ["dial", Phone, zh ? "拨号" : "Dial", 0, "softphone-tab-dial"],
    ["sms", MessageSquare, zh ? "短信" : "SMS", 0, "softphone-tab-sms"],
    ["inbox", Inbox, zh ? "收件箱" : "Inbox", smsUnread, "softphone-tab-inbox"],
  ].filter(Boolean);
  const curTab = chatTab ? "chat" : (tab === "chat" ? "dial" : tab);
  const expand = (path) => { nav(path || "/chat"); setOpen(false); };
  const phone = { zh, api, tab: curTab, ready, initing, err, setErr, num, setNum, status, muted, callerId, defaultCallerId, callerMode, cidEdit, setCidEdit, cidInput, setCidInput, cidBusy, saveCallerId, callee, setCallee, search, setSearch, results, setResults, searching, showResults, setShowResults, pickContact, addBulk, bulkList, setBulkList, replyFrom, setReplyFrom, setSmsUnread, normalizeE164, call, hangup, toggleMute, setTab };
  const FabIcon = isSub ? Phone : MessagesSquare;

  return (
    <CommunicationLayer>
      {!isSub && <ChatLightbox zh={zh} />}
      {incoming && (
        <div data-testid="softphone-incoming" className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-80 rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl p-6 text-center">
            <div className="mx-auto mb-3 h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center animate-pulse">
              <PhoneCall className="h-8 w-8 text-emerald-600" />
            </div>
            <div className="text-sm text-slate-500 mb-1">{zh ? "来电" : "Incoming call"}</div>
            <div data-testid="softphone-incoming-name" className="text-lg font-bold truncate">{incoming.name || (zh ? "未知客户" : "Unknown")}</div>
            <div data-testid="softphone-incoming-from" className="text-sm text-indigo-700 mb-5">{incoming.from}</div>
            <div className="flex items-center justify-center gap-4">
              <button data-testid="softphone-incoming-reject" onClick={rejectIncoming}
                className="h-14 w-14 rounded-full bg-red-600 hover:bg-red-500 text-white flex items-center justify-center transition-colors">
                <PhoneOff className="h-6 w-6" />
              </button>
              <button data-testid="softphone-incoming-accept" onClick={acceptIncoming}
                className="h-14 w-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center transition-colors">
                <Phone className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      )}

      {fab.fabVisible && !(open && mobile) && (
        // 入口在沟通内部也保持最高层级；键盘点击单独切换，指针继续沿用拖动/轻点判定。
        <div className="fixed z-[11000] transition-[left,top] duration-200" style={{ left: pos.x, top: pos.y }} onMouseEnter={fab.cancelRetract} onMouseLeave={() => { if (fab.dock && !open) fab.retractSoon(); }}>
          <button data-testid="softphone-fab" onPointerDown={fab.onPointerDown} data-open={open ? 1 : 0}
            aria-label={zh ? "沟通聊天" : "Open communications"} aria-expanded={open}
            onClick={(e) => { if (e.detail === 0) setOpen(o => !o); }}
            title={zh ? "沟通：聊天 · 拨号 · 短信（可拖动 · 拖到边缘贴边隐藏）" : "Comms: chat · dial · SMS (drag to move · drop at the edge to dock)"}
            className="relative h-14 w-14 rounded-full bg-brand-grad text-white shadow-brand flex items-center justify-center hover:scale-105 transition-transform touch-none select-none cursor-grab active:cursor-grabbing">
            <FabIcon className="h-6 w-6 pointer-events-none" />
            {status !== "idle" && <span data-testid="softphone-fab-incall" className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-red-500 border-2 border-white animate-pulse" />}
            {ready && <span data-testid="softphone-online-dot" title={zh ? "可接听来电" : "Online"}
              className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-500 border-2 border-white" />}
            {totalBadge > 0 && <span data-testid="comm-fab-badge" data-chat={chatUnread} data-sms={smsUnread} title={zh ? `${chatUnread} 条聊天未读 · ${smsUnread} 条未读短信` : `${chatUnread} chat · ${smsUnread} SMS unread`}
              className="absolute -top-1 -left-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white pointer-events-none">{totalBadge > 99 ? "99+" : totalBadge}</span>}
          </button>
          {!fab.dock && <button data-testid="softphone-hide" title={zh ? "贴边隐藏（鼠标移到边缘再弹出）" : "Dock to edge (hover the edge to show)"}
            onPointerDown={(e) => e.stopPropagation()} onClick={() => { setOpen(false); fab.hide(); }}
            className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white text-slate-500 border border-slate-200 shadow flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors">
            <X className="h-3 w-3" />
          </button>}
        </div>
      )}

      {fab.tabVisible && (
        <button data-testid="softphone-restore" data-side={fab.dock} onMouseEnter={() => fab.showPeek()} onClick={() => fab.showPeek(true)}
          title={zh ? "沟通窗（移到此处或点击弹出）" : "Comms (hover or tap to show)"}
          style={{ top: fab.tabTop }}
          className={`fixed z-[11000] h-12 w-5 bg-brand-grad text-white/90 shadow-brand flex items-center justify-center opacity-70 hover:opacity-100 hover:w-7 transition-[width,opacity] ${fab.dock === "left" ? "left-0 rounded-r-lg" : "right-0 rounded-l-lg"}`}>
          <FabIcon className="h-3.5 w-3.5" />
          {totalBadge > 0 && <span data-testid="softphone-restore-unread" className={`absolute top-0.5 h-2 w-2 rounded-full bg-red-500 ${fab.dock === "left" ? "right-0.5" : "left-0.5"}`} />}
        </button>
      )}

      {open && (fab.fabVisible || mobile) && (
        <div data-testid="softphone-panel" data-tab={curTab} data-mobile={mobile ? 1 : 0} data-maxed={!mobile && win.maxed ? 1 : 0} style={panelStyle}
          className={`fixed z-[9998] bg-white text-slate-900 shadow-2xl shadow-indigo-900/15 overflow-hidden flex flex-col ${mobile ? "" : "rounded-2xl border border-slate-200"} ${win.dragging ? "select-none" : ""}`}>
          {!mobile && <CommTitleBar Icon={FabIcon} zh={zh} maxed={win.maxed} dragging={win.dragging} title={`${zh ? "沟通" : "Comms"} · ${railItems.find((i) => i[0] === curTab)?.[2] || ""}`} popped={popped} onPopout={chatAvail ? () => (popped ? popReturn() : popOut(lastChatRef.current)) : null} onDragStart={win.onDragStart} onToggleMax={win.toggleMax} onMinimize={() => setOpen(false)} onClose={closeWin} />}
          <div className={`flex-1 min-h-0 flex flex-row ${win.dragging ? "pointer-events-none" : ""}`}>
            {!mobile && <CommRail items={railItems} tab={curTab} onTab={setTab} mobile={false} zh={zh} onReset={fab.reset} onResetWin={win.reset} onDragStart={win.onDragStart} />}
            <div className="flex-1 min-w-0 min-h-0 flex flex-col">
              {status !== "idle" && curTab !== "dial" && <CallBar status={status} who={callee || num} muted={muted} zh={zh} onMute={toggleMute} onHangup={hangup} onDial={() => setTab("dial")} />}
              {curTab === "chat"
                ? (popped && !mobile ? <PoppedOutPane zh={zh} onFocus={() => toPopout({ type: "focus" })} onReturn={popReturn} /> : <ChatDockPane api={api} user={user} zh={zh} mobile={mobile} openReq={openReq} onExpand={expand} onActive={(id) => { lastChatRef.current = id ? { channelId: id } : null; }} />)
                : <div className={`flex-1 min-h-0 flex flex-col w-full ${curTab === "inbox" || mobile ? "" : "max-w-[560px] mx-auto"}`}><PhonePane p={phone} /></div>}
            </div>
          </div>
          {mobile && <CommRail items={railItems} tab={curTab} onTab={setTab} mobile zh={zh} onClose={closeWin} />}
          {!mobile && !win.maxed && <ResizeHandles onResizeStart={win.onResizeStart} />}
        </div>
      )}
    </CommunicationLayer>
  );
}
